import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getCollaborateurs, getProfils, createCollaborateur, updateCollaborateur } from "../services/api";
import ModaleV2 from "../components/ModaleV2";
import IconeV2 from "../components/IconeV2";

// ===================================================================
// The Crowd v2 — Pilotage › Equipes
//
// Adaptation v2 de pages/GestionEquipe.js : memes appels API, memes
// regles (un directeur voit tout le monde, un manager son equipe et
// lui-meme), meme charge utile a la creation et a la modification.
//
// Changements d'affichage :
//  - une carte par collaborateur au lieu d'un tableau ; la carte ouvre
//    la fiche de suivi, les boutons Modifier / Desactiver restent
//    distincts ;
//  - formulaire d'ajout et de modification dans une modale ;
//  - les inactifs sont regroupes dans une section a part, estompes.
//
// La carte ouvre la fiche collaborateur v2 (/v2/pilotage/equipe/:id).
//
// Dispense de saisie des temps (Collaborateurs.saisie_temps, evolution 16) :
// case « Saisit ses temps » dans la modale, mention « Sans saisie » sur la
// carte. Absent (backend non a jour) = saisit ses temps.
//
// Les inactifs ne s'affichent que si services/api.js transmet le
// parametre de getCollaborateurs(false) au backend (actifs_only=false).
// ===================================================================

const FORM_VIDE = { nom: "", prenom: "", email: "", profil_id: "", role: "collaborateur", manager_id: "", jira_display_name: "", saisie_temps: true };

const LIBELLES_ROLES = { directeur: "Directeur", manager: "Manager", collaborateur: "Collaborateur" };

function initiales(c) {
  return `${(c.prenom || "")[0] || ""}${(c.nom || "")[0] || ""}`.toUpperCase();
}

function BadgeRole({ role }) {
  const r = role || "collaborateur";
  return <span className={`v2-statut ${r === "directeur" ? "v2-statut-fort" : "v2-statut-neutre"}`}>{LIBELLES_ROLES[r] || r}</span>;
}

// --- Carte collaborateur ---

function CarteCollaborateur({ c, onOuvrir, onModifier, onBasculerActif }) {
  const nomComplet = `${c.prenom} ${c.nom}`;
  return (
    <article className={`v2-collab${c.actif ? "" : " inactif"}`}>
      <button type="button" className="v2-collab-ouvrir" onClick={onOuvrir} aria-label={`Ouvrir la fiche de ${nomComplet}`}>
        <span className="v2-collab-entete">
          <span className="v2-collab-avatar" aria-hidden="true">{initiales(c)}</span>
          <span className="v2-collab-identite">
            <span className="v2-collab-nom">{c.prenom} <span className="v2-collab-nom-famille">{c.nom}</span></span>
            <span className="v2-collab-profil">{c.profil_nom || "—"}</span>
          </span>
          <IconeV2 nom="chevronDroite" className="v2-collab-chevron" />
        </span>
        <span className="v2-collab-details">
          <span className="v2-collab-ligne">
            <BadgeRole role={c.role} />
            {!c.actif && <span className="v2-statut v2-statut-neutre">Inactif</span>}
            {c.saisie_temps === false && <span className="v2-statut v2-statut-neutre">Sans saisie</span>}
          </span>
          <span className="v2-collab-ligne v2-collab-discret" title={c.email}>{c.email}</span>
          <span className="v2-collab-ligne v2-collab-discret">Manager : {c.manager_nom || "—"}</span>
        </span>
      </button>

      <div className="v2-collab-actions">
        <button type="button" className="v2-btn v2-btn-secondaire" onClick={onModifier}>
          <IconeV2 nom="crayon" /> Modifier
        </button>
        <button type="button" className={`v2-btn v2-btn-secondaire${c.actif ? " v2-btn-danger-texte" : ""}`} onClick={onBasculerActif}>
          {c.actif ? "Désactiver" : "Réactiver"}
        </button>
      </div>
    </article>
  );
}

// --- Modale formulaire ---

function ModaleCollaborateur({ edition, form, setForm, profils, managers, onValider, onClose }) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const champ = (cle) => (e) => setForm({ ...form, [cle]: e.target.value });

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider();
    } catch (err) {
      setErreur(err.message || "Erreur inattendue.");
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre={edition ? "Modifier le collaborateur" : "Nouveau collaborateur"}
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours}>
            {enCours ? "Enregistrement…" : edition ? "Enregistrer les modifications" : "Enregistrer"}
          </button>
        </>
      }
    >
      <div className="v2-champs-ligne">
        <div className="v2-champ">
          <label className="v2-label" htmlFor="v2-collab-prenom">Prénom</label>
          <input id="v2-collab-prenom" value={form.prenom} onChange={champ("prenom")} autoFocus />
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="v2-collab-nom">Nom</label>
          <input id="v2-collab-nom" value={form.nom} onChange={champ("nom")} />
        </div>
      </div>

      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-collab-email">E-mail</label>
        <input id="v2-collab-email" type="email" value={form.email} onChange={champ("email")} />
      </div>

      <div className="v2-champs-ligne">
        <div className="v2-champ">
          <label className="v2-label" htmlFor="v2-collab-profil">Profil</label>
          <select id="v2-collab-profil" value={form.profil_id} onChange={champ("profil_id")}>
            <option value="">— Choisir un profil —</option>
            {profils.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
          </select>
        </div>
        <div className="v2-champ">
          <label className="v2-label" htmlFor="v2-collab-role">Rôle</label>
          <select id="v2-collab-role" value={form.role} onChange={champ("role")}>
            <option value="collaborateur">Collaborateur</option>
            <option value="manager">Manager</option>
            <option value="directeur">Directeur</option>
          </select>
        </div>
      </div>

      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-collab-manager">Manager</label>
        <select id="v2-collab-manager" value={form.manager_id} onChange={champ("manager_id")}>
          <option value="">— Pas de manager (directeur) —</option>
          {managers.map((m) => <option key={m.id} value={m.id}>{m.prenom} {m.nom} ({m.role})</option>)}
        </select>
      </div>

      <div className="v2-champ">
        <label className="v2-label" htmlFor="v2-collab-jira">Nom Jira</label>
        <input id="v2-collab-jira" value={form.jira_display_name} onChange={champ("jira_display_name")} placeholder="ex : Prénom NOM - Société" />
      </div>

      <label className="v2-case" htmlFor="v2-collab-saisie">
        <input
          id="v2-collab-saisie"
          type="checkbox"
          checked={form.saisie_temps}
          onChange={(e) => setForm({ ...form, saisie_temps: e.target.checked })}
        />
        <span>
          <span className="v2-case-libelle">Saisit ses temps</span>
          <span className="v2-champ-aide">Décoché : pas de Saisie des temps, exclu des relances et des indicateurs de saisie.</span>
        </span>
      </label>

      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
    </ModaleV2>
  );
}

// --- Page ---

function GestionEquipeV2({ collaborateur }) {
  const navigate = useNavigate();
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [profils, setProfils] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [modaleOuverte, setModaleOuverte] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(FORM_VIDE);
  const [message, setMessage] = useState(null); // { type, texte }

  const loadData = useCallback(() => {
    setErreurChargement(null);
    getCollaborateurs(false)
      .then((data) => {
        setCollaborateurs(data);
        setManagers(data.filter((c) => c.role === "directeur" || c.role === "manager"));
      })
      .catch((err) => setErreurChargement(`Collaborateurs non chargés : ${err.message}`))
      .finally(() => setLoading(false));
    getProfils().then(setProfils).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const estDirecteur = collaborateur?.role === "directeur";
  const visibles = estDirecteur
    ? collaborateurs
    : collaborateurs.filter((c) => c.manager_id === collaborateur?.id || c.id === collaborateur?.id);
  const actifs = visibles.filter((c) => c.actif);
  const inactifs = visibles.filter((c) => !c.actif);

  const ouvrirCreation = () => {
    setEditingId(null);
    setForm(FORM_VIDE);
    setMessage(null);
    setModaleOuverte(true);
  };

  const ouvrirEdition = (c) => {
    setEditingId(c.id);
    setForm({
      nom: c.nom,
      prenom: c.prenom,
      email: c.email,
      profil_id: String(c.profil_id),
      role: c.role || "collaborateur",
      manager_id: c.manager_id ? String(c.manager_id) : "",
      jira_display_name: c.jira_display_name || "",
      saisie_temps: c.saisie_temps !== false,
    });
    setMessage(null);
    setModaleOuverte(true);
  };

  const fermerModale = () => {
    setModaleOuverte(false);
    setEditingId(null);
    setForm(FORM_VIDE);
  };

  // Levent en cas d'erreur : la modale affiche le message et reste ouverte
  const enregistrer = async () => {
    if (editingId) {
      await updateCollaborateur(editingId, {
        nom: form.nom,
        prenom: form.prenom,
        email: form.email,
        profil_id: parseInt(form.profil_id),
        role: form.role,
        manager_id: form.manager_id ? parseInt(form.manager_id) : null,
        jira_display_name: form.jira_display_name || null,
        saisie_temps: form.saisie_temps,
      });
      setMessage({ type: "succes", texte: "Collaborateur mis à jour." });
    } else {
      await createCollaborateur({
        ...form,
        profil_id: parseInt(form.profil_id),
        manager_id: form.manager_id ? parseInt(form.manager_id) : null,
        jira_display_name: form.jira_display_name || null,
      });
      setMessage({ type: "succes", texte: "Collaborateur ajouté." });
    }
    fermerModale();
    loadData();
  };

  const basculerActif = async (c) => {
    setMessage(null);
    try {
      await updateCollaborateur(c.id, { actif: !c.actif });
      loadData();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Erreur : ${err.message}` });
    }
  };

  const ouvrirFiche = (c) => navigate(`/v2/pilotage/equipe/${c.id}`);

  const grille = (liste) => (
    <div className="v2-collab-grille">
      {liste.map((c) => (
        <CarteCollaborateur
          key={c.id}
          c={c}
          onOuvrir={() => ouvrirFiche(c)}
          onModifier={() => ouvrirEdition(c)}
          onBasculerActif={() => basculerActif(c)}
        />
      ))}
    </div>
  );

  return (
    <div className="v2-page v2-page-module">
      <div className="v2-bib-entete">
        <header className="v2-module-entete">
          <h1 className="v2-module-titre">Équipes</h1>
          <p className="v2-sous-titre">
            {estDirecteur ? "Toute l'équipe" : "Mon équipe"} — {actifs.length} actif{actifs.length > 1 ? "s" : ""}
            {inactifs.length > 0 && `, ${inactifs.length} inactif${inactifs.length > 1 ? "s" : ""}`}
          </p>
        </header>
        <button type="button" className="v2-btn v2-btn-principal" onClick={ouvrirCreation}>
          <IconeV2 nom="plus" /> Ajouter un collaborateur
        </button>
      </div>

      {erreurChargement && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreurChargement}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      {loading ? (
        <div className="v2-chargement">Chargement…</div>
      ) : (
        <>
          {actifs.length === 0 ? <div className="v2-panneau"><p className="v2-vide">Aucun collaborateur actif.</p></div> : grille(actifs)}

          {inactifs.length > 0 && (
            <section className="v2-collab-section">
              <div className="v2-section-titre">
                <h2>Inactifs</h2>
                <span className="v2-section-filet" />
              </div>
              {grille(inactifs)}
            </section>
          )}
        </>
      )}

      {modaleOuverte && (
        <ModaleCollaborateur
          edition={!!editingId}
          form={form}
          setForm={setForm}
          profils={profils}
          managers={managers}
          onValider={enregistrer}
          onClose={fermerModale}
        />
      )}
    </div>
  );
}

export default GestionEquipeV2;
